import React, { useState } from 'react';
import { Box, Alert, useTheme, Divider } from '@mui/material';
import {
  DeliveryStatus as DeliveryStatusEnum,
  MealOptionType,
} from '@fdha/graphql-api-admin';
import { SectionType } from '@fdha/common-utils';
import { Loader } from '@fdha/web-ui-library';

import { useReviewDeliveryData } from '../../hooks/useMealDeliveryContext';
import { useDeliveryDialog } from '../../hooks';
import { DishDetails } from '../../pages/patient/delivery/DishDetails';

import { getStyles } from './ReviewDelivery.styles';

import { MealSection, DeliveryFooter, DeliveryEmptyState } from './index';

interface ReviewDeliveryProps {
  deliveryId: string;
  patientId?: string;
  foodProgramUserId: string;
  deliveryMenuId: string;
  status?: DeliveryStatusEnum | null;
  isCanceled?: boolean;
  showWeekMenu?: boolean;
  onAddItems?: (itemType: SectionType) => void;
}

interface MealItem {
  dishId: string;
  menuOptionId: string;
  name: string;
  photoUrl?: string;
  quantity: number;
  mealkitVariantId?: string;
  mealkitVariantIngredientName?: string;
  mealOptionType?: string;
}

interface DishDetailsData {
  open: boolean;
  dishId: string;
  mealOptionType: MealOptionType;
  selectedVariantId?: string;
  selectedCount: number;
  requiredCount: number;
  menuOptionId: string;
  sectionName: SectionType;
}

const ReviewDelivery: React.FC<ReviewDeliveryProps> = ({
  deliveryId,
  patientId,
  foodProgramUserId,
  deliveryMenuId,
  status = DeliveryStatusEnum.Open,
  isCanceled = false,
  showWeekMenu = false,
  onAddItems,
}) => {
  const theme = useTheme();
  const styles = getStyles(theme);

  const { openCancelDialog, openReactivateDialog } = useDeliveryDialog(
    patientId || ''
  );

  // Determine delivery state - following mobile's pattern
  const isOpen = status === DeliveryStatusEnum.Open;
  const isReadOnly = !showWeekMenu && (!isOpen || isCanceled);

  // Use unified delivery data from common hook (handles both open and closed)
  const deliveryData = useReviewDeliveryData(showWeekMenu);
  const { hasMultipleVariants } = deliveryData;

  // Determine loading state
  const loading = deliveryData.loading;

  const [dishDetailsData, setDishDetailsData] = useState<DishDetailsData>({
    open: false,
    dishId: '',
    mealOptionType: MealOptionType.Dish,
    selectedCount: 0,
    requiredCount: 1,
    sectionName: 'entreeDishes',
    menuOptionId: '',
  });

  // Mobile-style handlers
  const hasSides = () => {
    if (!deliveryData.snacksAndSides) return false;

    return deliveryData.snacksAndSides.some(
      (item: any) =>
        item.name?.toLowerCase().includes('side') ||
        item.mealOptionType?.toString().toLowerCase().includes('side')
    );
  };

  const handleQuantityChange = (
    menuOptionId: string,
    quantity: number,
    mealkitVariantId?: string
  ) => {
    if (!isReadOnly && deliveryData.updateSelection) {
      // For open deliveries, use shared state actions
      const isEntree = [
        ...(deliveryData.preparedMeals || []),
        ...(deliveryData.mealkits || []),
      ].some((item: any) => item.menuOptionId === menuOptionId);

      deliveryData.updateSelection({
        isEntree,
        menuOptionId,
        quantity,
        mealkitVariantId,
      });
    }
    // For closed deliveries, quantity changes are not supported (read-only)
  };

  const handleItemClick = (item: MealItem) => {
    if (!deliveryData) return;

    // Determine if it's an entree or snack to get the right counts
    const isEntree =
      deliveryData.preparedMeals.some(
        (meal: any) => meal.menuOptionId === item.menuOptionId
      ) ||
      deliveryData.mealkits.some(
        (meal: any) => meal.menuOptionId === item.menuOptionId
      );

    const currentDisplay = isEntree
      ? deliveryData.entreeDisplay
      : deliveryData.snacksDisplay;

    // Determine meal option type
    const mealOptionType =
      item.mealOptionType === MealOptionType.MealKit
        ? MealOptionType.MealKit
        : MealOptionType.Dish;

    setDishDetailsData({
      open: true,
      dishId: item.dishId,
      mealOptionType,
      selectedVariantId: item.mealkitVariantId,
      selectedCount: currentDisplay.selected,
      requiredCount: currentDisplay.total,
      menuOptionId: item.menuOptionId,
      sectionName: isEntree ? 'entreeDishes' : 'snacks',
    });
  };

  const handleCloseDishDetails = () => {
    setDishDetailsData((prev) => ({ ...prev, open: false }));
  };

  // Mobile-style renderMenuSection function
  const renderMenuSection = () => {
    if (!deliveryData) return null;

    const hasSelections =
      deliveryData.entreeDisplay.selected > 0 ||
      deliveryData.snacksDisplay.selected > 0;

    // Show empty state for closed deliveries with no selections
    if (isReadOnly && !hasSelections) {
      return (
        <DeliveryEmptyState
          isCanceled={isCanceled}
          isClosedDelivery={status === DeliveryStatusEnum.Closed}
        />
      );
    }

    return (
      <>
        <MealSection
          title="Entree dishes"
          sectionType="entreeDishes"
          hasMultipleVariants={hasMultipleVariants}
          preparedItems={deliveryData.preparedMeals as MealItem[]}
          mealKitItems={deliveryData.mealkits as MealItem[]}
          selectedCount={deliveryData.entreeDisplay.selected}
          requiredCount={deliveryData.entreeDisplay.total}
          isReadOnly={isReadOnly}
          onAddItems={onAddItems}
          onQuantityChange={handleQuantityChange}
          onItemClick={handleItemClick}
          showAddButton={!isReadOnly}
          addButtonText="ADD ENTREES"
          showMealTypeSeparation={false}
        />

        {deliveryData.snacksDisplay.total > 0 && (
          <>
            <Divider sx={styles.sectionDivider} />
            <MealSection
              title="Snacks and sides"
              sectionType="snacks"
              hasMultipleVariants={hasMultipleVariants}
              items={deliveryData.snacksAndSides as MealItem[]}
              selectedCount={deliveryData.snacksDisplay.selected}
              requiredCount={deliveryData.snacksDisplay.total}
              isReadOnly={isReadOnly}
              onQuantityChange={handleQuantityChange}
              onAddItems={onAddItems}
              onItemClick={handleItemClick}
              showAddButton={!isReadOnly}
              addButtonText="ADD SNACKS/SIDES"
              showMealTypeSeparation={false}
            />
          </>
        )}
      </>
    );
  };

  // Check if we should show empty state
  const hasSelections = deliveryData
    ? deliveryData.entreeDisplay.selected > 0 ||
      deliveryData.snacksDisplay.selected > 0
    : false;
  const showingEmptyState = isReadOnly && !hasSelections;

  if (loading) {
    return <Loader />;
  }

  // Check for actual errors from the delivery data
  const error = deliveryData.error;
  if (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown error occurred';
    return (
      <Box sx={styles.errorContainer}>
        <Alert severity="error">
          Error loading meal options: {errorMessage}
        </Alert>
      </Box>
    );
  }

  const selectedDishItem = {
    dishId: dishDetailsData.dishId,
    mealOptionType: dishDetailsData.mealOptionType,
    menuOptionId: dishDetailsData.menuOptionId,
    selectedVariantId: dishDetailsData.selectedVariantId,
    sectionName: dishDetailsData.sectionName,
  };

  const onSave = () => {
    if (isReadOnly) {
      return;
    }

    deliveryData.saveSelections({
      foodProgramUserId,
      deliveryMenuId,
    });
  };
  return (
    <>
      {showingEmptyState ? (
        // Empty state without margin - let footer handle spacing
        renderMenuSection()
      ) : (
        // Regular content with white card background but no bottom margin
        <Box
          sx={{
            ...styles.mainContainer,
            marginBottom: 0, // Remove bottom margin since footer follows
          }}
        >
          {renderMenuSection()}
        </Box>
      )}

      {/* Footer with summary and actions - always in its own container */}
      <Box
        sx={{
          ...styles.mainContainer,
          marginBottom: 0, // Remove bottom margin since this is the last element
          ...(showingEmptyState && {
            borderRadius: theme.spacing(0.625), // Add rounded corners when showing empty state
          }),
        }}
      >
        <DeliveryFooter
          entreesSelected={deliveryData.entreeDisplay.selected}
          entreesTotal={deliveryData.entreeDisplay.total}
          snacksSelected={deliveryData.snacksDisplay.selected}
          snacksTotal={deliveryData.snacksDisplay.total}
          hasSides={hasSides()}
          isDirty={deliveryData.isDirty}
          isSaving={deliveryData.isSaving}
          isReadOnly={isReadOnly}
          isCanceled={isCanceled}
          isCancellable={!showWeekMenu}
          showingEmptyState={showingEmptyState}
          onSave={onSave}
          onCancel={() => {
            if (deliveryId) {
              openCancelDialog(deliveryId);
            }
          }}
          onReactivate={() => {
            if (deliveryId) {
              openReactivateDialog(deliveryId);
            }
          }}
        />
      </Box>

      {/* Dish Details Modal */}
      {dishDetailsData.open && (
        <DishDetails
          dishItem={selectedDishItem}
          selectedCount={dishDetailsData.selectedCount}
          requiredCount={dishDetailsData.requiredCount}
          onClose={handleCloseDishDetails}
          isPreviewMode={true}
          open={dishDetailsData.open}
        />
      )}
    </>
  );
};

export default ReviewDelivery;
